import {
  PER_PAGE,
  PER_PAGE_OPTIONS,
  WORKFLOW_INSTANCES_STATUS_FILTER,
  WORKFLOW_INSTANCE_APPROVAL_STATUSES,
  WORKFLOW_INSTANCE_APPROVAL_STATUS_VALUES,
  WORKFLOW_INSTANCE_STATUSES,
  WORKFLOW_APPROVAL_ACTION_VALUES,
} from '@microsec/constants';
import { Component, effect, Input, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { WorkflowService } from '@microsec/services';
import { MenuItem } from 'primeng/api';
import { Observable, catchError, finalize, forkJoin, of, switchMap } from 'rxjs';
import { CommonToolbarConfiguration, CommonToolbarResult } from '@microsec/models';
import { CardComponent, CommonToolbarComponent, LoadingOverlayComponent } from '@microsec/components';
import { ButtonModule } from 'primeng/button';
import { DataViewModule } from 'primeng/dataview';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { SplitButtonModule } from 'primeng/splitbutton';

const FILTER_CONFIG: CommonToolbarConfiguration = {
  types: ['search', 'filter'],
  hideResetSortOption: true,
  showTotalRecords: true,
  totalRecordsIcon: 'fa fa-diagram-next',
  searchPlaceholder: 'Search Workflow Name, Description, and Instance Status...',
  filters: {
    1: {
      key: 'status',
      label: 'Instance Status',
      type: 'dropdown',
      options: [...WORKFLOW_INSTANCES_STATUS_FILTER],
    },
    2: {
      key: 'approvalStatus',
      label: 'Approval Status',
      type: 'dropdown',
      options: [...WORKFLOW_INSTANCE_APPROVAL_STATUSES],
    },
  },
};

const DISPLAY_INSTANCE_STATUSES = {
  SUCCESS: 'Success',
  PARTIAL_SUCCESS: 'Partial Success',
  FAILURE: 'Failure',
  RUNNING: 'Running',
  PENDING_APPROVAL: 'Approval Required',
  DENIED: 'Approval Denied',
};

@Component({
  selector: 'app-mitigation-workflow',
  templateUrl: './mitigation-workflow.component.html',
  styleUrls: ['./mitigation-workflow.component.scss'],
  imports: [
    CommonModule,
    ButtonModule,
    DataViewModule,
    SplitButtonModule,
    TooltipModule,
    LoadingOverlayComponent,
    CommonToolbarComponent,
    CardComponent,
    TitleCasePipe,
  ],
  providers: [TitleCasePipe],
})
export class MitigationWorkflowComponent extends BaseComponent implements OnInit {
  isLoading = false;

  /* Pagination */

  PER_PAGE = PER_PAGE;

  PER_PAGE_OPTIONS = PER_PAGE_OPTIONS;

  /* Threat Details */

  _threat = null;

  get threat() {
    return this._threat;
  }

  @Input() set threat(value: any) {
    this._threat = value;
    if (!!this.threat?.threat_id) {
      this.getInstances();
    }
  }

  _threatGroup: any = null;

  get threatGroup() {
    return this._threatGroup;
  }

  @Input() set threatGroup(value: any) {
    this._threatGroup = value;
    if (!!this.threatGroup?.threats?.length) {
      this.getInstances();
    }
  }

  /* All Workflow Instances */

  allInstances: any[] = [];

  selectedApprovalInstance: any = null;

  /* FilterObject */

  filterConfiguration: CommonToolbarConfiguration = FILTER_CONFIG;

  filterObject: WritableSignal<CommonToolbarResult | null> = signal({
    search: '',
    sort: null,
    filter: {},
    isFiltered: false,
    isSortReset: false,
  });

  /* Split Button on Approval or Deny */

  approveButtonItems: MenuItem[] = [
    {
      label: 'Approve',
      command: () => {
        this.handleInstanceApproval(WORKFLOW_APPROVAL_ACTION_VALUES.APPROVE, null);
      },
    },
    { separator: true },
    {
      label: 'Deny',
      command: () => {
        this.handleInstanceApproval(WORKFLOW_APPROVAL_ACTION_VALUES.DENY, null);
      },
    },
  ];

  WORKFLOW_INSTANCE_STATUSES = WORKFLOW_INSTANCE_STATUSES;

  DISPLAY_INSTANCE_STATUSES = DISPLAY_INSTANCE_STATUSES;

  WORKFLOW_APPROVAL_ACTION_VALUES = WORKFLOW_APPROVAL_ACTION_VALUES;

  @ViewChild('dv') dv!: DataView;

  constructor(private workflowSrv: WorkflowService) {
    super();
    effect(() => {
      const values = this.filterObject();
      if (!!values) {
        this.assignFilters(!!values?.isFiltered ? values : {});
        this.getInstances();
      }
    });
  }

  async ngOnInit() {
    await this.prepareConfigs();
  }

  /**
   * Assign filters
   * @param value
   */
  private assignFilters(value: any) {
    if (!!value) {
      this.filterObject.update((filterObject) => {
        if (!!filterObject) {
          filterObject.filter = {
            ...(value?.filter || {}),
            search: value?.search || undefined,
          };
        }
        return filterObject;
      });
    }
  }

  /**
   * Get Instances of all workflows and on each instance call workflow/{id} to get workflow name and description
   * @param event
   */
  async getInstances() {
    await this.prepareConfigs();
    this.isLoading = true;
    const threatIds: any[] = !!this.threat?.threat_id
      ? [this.threat?.threat_id]
      : !!this.threatGroup?.threats?.length
        ? this.threatGroup?.threats.map((threat: any) => threat.threat_id)
        : [];
    const requests = threatIds.map((threatId) =>
      this.workflowSrv.getWorkflowInstances({
        organizationId: this.breadcrumbConfig?.organizationId,
        projectId: this.breadcrumbConfig?.projectId,
        search: this.filterObject()?.filter?.search,
        threatId: threatId,
        status: this.filterObject()?.filter?.status,
        triggeredBy: this.filterObject()?.filter?.triggeredBy,
        approvalStatus: this.filterObject()?.filter?.approvalStatus,
      }),
    );

    forkJoin(requests)
      .pipe(
        switchMap((res) => {
          const allInstances = (res || []).map((r) => r.data).flat();
          const filteredInstances = allInstances.filter((value, index, self) => index === self.findIndex((t) => t.workflow_id === value.workflow_id));
          const workflowIds = filteredInstances.map((instance: any) => instance.workflow_id);

          if (workflowIds.length === 0) {
            return of(res);
          }

          const workflowRequests = workflowIds.map((workflowId: any) => {
            return this.workflowSrv.getWorkflow(workflowId);
          });

          return forkJoin(workflowRequests).pipe(
            catchError((error): Observable<any> => {
              console.error('Error fetching workflow details:', error);
              return of([]);
            }),
            switchMap((workflowDetails: any[]) => {
              const updatedInstances = filteredInstances.map((instance: any, index: number) => {
                const workflowDetail = workflowDetails[index];
                return {
                  ...instance,
                  workflowName: workflowDetail.name,
                  workflowDescription: workflowDetail.description,
                  displayStatus: this.getDisplayInstanceStatus(instance.status, instance.approval),
                };
              });

              return of({ ...res, data: updatedInstances });
            }),
          );
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (res: any) => {
          this.filterConfiguration.totalRecords = res?.total_records || 0;
          this.allInstances = (res?.data as any[]) || [];
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  /**
   * Approve or Deny instance for manual workflow
   * @param action // 'approved' or 'denied'
   * @param instance
   */
  handleInstanceApproval(action: string, instance?: any) {
    this.isLoading = true;
    const payload =
      action === WORKFLOW_APPROVAL_ACTION_VALUES.APPROVE
        ? { approval_status: WORKFLOW_INSTANCE_APPROVAL_STATUS_VALUES.APPROVED }
        : { approval_status: WORKFLOW_INSTANCE_APPROVAL_STATUS_VALUES.DENIED };
    const instanceId = !!instance ? instance.id : this.selectedApprovalInstance.id;
    const workflowId = !!instance ? instance.workflow_id : this.selectedApprovalInstance.workflow_id;
    this.workflowSrv
      .patchWorkflowInstance(workflowId, instanceId, payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.showSuccessMessage('Instance is ' + action + ' successfully!');
          this.getInstances();
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  /**
   * Rerun instance when instance has been completed
   * @param event
   * @param instance
   */
  rerunInstance(event: any, instance: any) {
    this.isLoading = true;
    this.workflowSrv
      .rerunWorkflowInstance(instance.workflow_id, instance.id)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.getInstances();
        },
        error: (error) => {
          this.showErrorMessage(error);
        },
      });
  }

  /**
   * Navigate To Workflow Details Page and call the GET workflow/{id}
   * @param workflowId
   */
  navigateToWorkflow(workflowId: any) {
    if (!!workflowId) {
      this.isLoading = true;
      this.workflowSrv
        .getWorkflow(workflowId)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (rs) => {
            this.workflowSrv.selected = rs;
            this.changeRoute(`organization/${this.breadcrumbConfig?.organizationId}/project/${this.breadcrumbConfig?.projectId}/workflows/details`);
          },
          error: (error) => {
            this.showErrorMessage(error);
          },
        });
    } else {
      this.showErrorMessage('Unable to get workflow id from instance');
    }
  }

  /**
   * Approval Split Button Dropdown Clicked
   * @param event // browser event
   * @param instance // instance data
   */
  onSBDClicked(event: any, instance: any) {
    event.stopPropagation();
    this.selectedApprovalInstance = instance;
  }

  /**
   * Get appropriate instance status for workflow instance to display
   * @param status
   * @param approval
   */
  getDisplayInstanceStatus(status: string, approval: any): string {
    switch (status) {
      case WORKFLOW_INSTANCE_STATUSES.SUCCESS.MESSAGE: {
        return DISPLAY_INSTANCE_STATUSES.SUCCESS;
      }
      case WORKFLOW_INSTANCE_STATUSES.PARTIAL_SUCCESS.MESSAGE: {
        return DISPLAY_INSTANCE_STATUSES.PARTIAL_SUCCESS;
      }
      case WORKFLOW_INSTANCE_STATUSES.FAILURE.MESSAGE: {
        return DISPLAY_INSTANCE_STATUSES.FAILURE;
      }
      case WORKFLOW_INSTANCE_STATUSES.RUNNING.MESSAGE: {
        return DISPLAY_INSTANCE_STATUSES.RUNNING;
      }
      case WORKFLOW_INSTANCE_STATUSES.APPROVAL_REQUIRED.MESSAGE: {
        if (approval.approval_status === WORKFLOW_INSTANCE_APPROVAL_STATUS_VALUES.PENDING) {
          return DISPLAY_INSTANCE_STATUSES.PENDING_APPROVAL;
        }
        if (approval.approval_status === WORKFLOW_INSTANCE_APPROVAL_STATUS_VALUES.DENIED) {
          return DISPLAY_INSTANCE_STATUSES.DENIED;
        }
        return '';
      }
      default: {
        return '';
      }
    }
  }
}
